import { cn } from '@/lib/utils';
import type { PaymentType } from '@/services/api/frontdesk.api';

/**
 * Visual mapping for a booking's payment type. `null` (or absent) means the
 * front-desk hasn't set it yet.
 *
 * - prepaid_ota      → green  "Prepaid via OTA"
 * - pay_at_hotel     → amber  "Collect at desk"
 * - bill_to_company  → blue   "Bill to company"
 * - null             → gray   "Payment: not set"
 */
interface PaymentTypeMeta {
  label: string;
  dot: string;
  bg: string;
  text: string;
}

export const PAYMENT_TYPE_CONFIG: Record<PaymentType, PaymentTypeMeta> = {
  prepaid_ota: { label: 'Prepaid via OTA', dot: 'bg-green-500', bg: 'bg-green-100', text: 'text-green-700' },
  pay_at_hotel: { label: 'Collect at desk', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-800' },
  bill_to_company: { label: 'Bill to company', dot: 'bg-blue-500', bg: 'bg-blue-100', text: 'text-blue-700' },
};

const NOT_SET_META: PaymentTypeMeta = {
  label: 'Payment: not set',
  dot: 'bg-gray-400',
  bg: 'bg-gray-100',
  text: 'text-gray-600',
};

/** Resolve the visual metadata for a payment type (null → "not set"). */
export function paymentTypeMeta(paymentType: PaymentType | null | undefined): PaymentTypeMeta {
  return paymentType ? PAYMENT_TYPE_CONFIG[paymentType] : NOT_SET_META;
}

/** Options for the set-control, in display order (last = clear to "not set"). */
export const PAYMENT_TYPE_OPTIONS: { value: PaymentType | null; label: string }[] = [
  { value: 'prepaid_ota', label: 'Prepaid via OTA' },
  { value: 'pay_at_hotel', label: 'Collect at desk' },
  { value: 'bill_to_company', label: 'Bill to company' },
  { value: null, label: 'Not set' },
];

interface PaymentTypeBadgeProps {
  paymentType: PaymentType | null | undefined;
  className?: string;
}

/**
 * Small, reusable colored pill showing a booking's payment type.
 * Read-only; the editable control lives in `PaymentTypeControl`.
 */
export function PaymentTypeBadge({ paymentType, className }: PaymentTypeBadgeProps) {
  const meta = paymentTypeMeta(paymentType);

  return (
    <span
      aria-label={`Payment type: ${meta.label}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        meta.bg,
        meta.text,
        className,
      )}
    >
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
}
